<template>
  <DualChip
    class="shipment-chip"
    :left-label="statusLabel"
    :left-severity="statusSeverity"
    :right-label="healthLabel"
    :right-severity="healthSeverity"
  />
</template>

<script lang="ts" setup>
import { computed, PropType, ref, toRefs, watch } from 'vue';

import { translateEnum } from '@rygen/rygen-ui/i18n';
import { Severity } from '@rygen/rygen-ui/model';

import { ShipmentHealth, ShipmentStatus } from '@/model';

import DualChip from '@/components/ui/DualChip.vue';

const props = defineProps({
  status: {
    type: String as PropType<ShipmentStatus>,
    default: () => 'LOADING',
  },
  health: {
    type: String as PropType<ShipmentHealth>,
    default: () => 'LOADING',
  },
});
const { status, health } = toRefs(props);
const emit = defineEmits<{
  (event: 'update:health', newVal: ShipmentHealth): void;
}>();

const internalHealth = ref(health.value);

const statusLabel = computed(() => {
  return translateEnum(status.value, 'ShipmentStatus');
});

const healthLabel = computed(() => {
  return translateEnum(health.value, 'ShipmentHealth');
});

const statusSeverity = computed((): Severity | undefined => {
  switch (status.value) {
    case ShipmentStatus.COMPLETE:
      return Severity.INFO;
    case ShipmentStatus.IN_TRANSIT:
    case ShipmentStatus.DELIVERED:
      return Severity.SUCCESS_LIGHT;
    case ShipmentStatus.BOOKED:
      return Severity.INFO;
    case ShipmentStatus.PLANNING:
    case ShipmentStatus.CARRIER_ASSIGNED:
    case ShipmentStatus.TENDERED:
      return Severity.INFO_LIGHT;
    case ShipmentStatus.TENDER_REJECTED:
      return Severity.DANGER_LIGHT;
    default:
      return undefined;
  }
});

const healthSeverity = computed((): Severity | undefined => {
  switch (health.value) {
    case ShipmentHealth.HEALTHY:
      return Severity.INFO;
    case ShipmentHealth.AT_RISK:
      return Severity.WARNING;
    case ShipmentHealth.CRITICAL:
      return Severity.DANGER;
    default:
      return undefined;
  }
});

watch(
  health,
  (newVal: ShipmentHealth) => {
    internalHealth.value = ShipmentHealth[newVal];
  },
  { immediate: true },
);

watch(internalHealth, (newVal: ShipmentHealth) => {
  emit('update:health', newVal);
});
</script>
